import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, inject, Injector, Input, OnDestroy, OnInit, Output, signal, ViewChild } from '@angular/core'

import { BehaviorSubject, combineLatest, debounceTime, firstValueFrom, from, map, Observable, of, startWith, Subject, switchMap, take, takeUntil } from 'rxjs'

import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { Tilikausi, TilinpaatoksenTuloslaskelmanTyyppi } from 'app/_jaettu-lemonator/model/asiakas'

import { Validators } from '@angular/forms'
import { disabled, FieldTree, form } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { DateService } from '@lemon/core/service/date.service'
import { StringService } from '@lemon/core/service/string.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { TilinpaatosLiitetiedot, TilinpaatosLiitetiedotKentat } from 'app/_jaettu-lemonator/model/kirjanpito'
import { TilinpaatosStatus } from 'app/_jaettu-lemonator/model/tilinpaatos'
import {
  ListausTilintarkastaja,
  TilintarkastajatAnnaListaRequest,
  TilintarkastajatAnnaListaResponse,
  TilintarkastajatAsetaOikeusRequest,
  TilintarkastajatAsetaOikeusResponse,
} from 'app/_jaettu-lemonator/model/tilintarkastaja'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import { TilinpaatosLiitetiedotJaettuService } from 'app/_jaettu-lemonator/service/tilinpaatos-liitetiedot-jaettu.service'
import { TilintarkastajaUriService } from 'app/_jaettu-lemonator/service/tilintarkastaja-uri.service'
import { TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_TAIKALINKKI } from 'app/_jaettu-lemonator/service/tilintarkastaja.service'
import { arvonMuutokset, asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { lemonShare } from '../../_jaettu-angular/_rxjs/lemon-share.operator'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { TilintarkastajaKutsuDialogComponent } from './tilintarkastaja-kutsu-dialog.component'

interface AloitusLomake {
  taseenLoppusummaYli: boolean | null
  liikevaihtoYli: boolean | null
  henkilostoYli: boolean | null
  tilinpaatoksenTuloslaskelma: TilinpaatoksenTuloslaskelmanTyyppi | null
  tilintarkastetaan: boolean | null
  tilintarkastajat: ListausTilintarkastaja[]
  allekirjoitetaanLemonaidissa: boolean | null
}

@Component({
  selector: '[app-kirjanpito-tilinpaatos-aloitus]',
  templateUrl: './tilinpaatos-aloitus.component.html',
  styleUrls: ['./tilinpaatos-aloitus.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KirjanpitoTilinpaatosAloitusComponent implements OnInit, OnDestroy {
  @Input() selectedTilikausiObservable: Observable<Tilikausi>
  @Input() tilinpaatosStatusObservable: Observable<TilinpaatosStatus>
  @Input() liitetiedotObservable: Observable<TilinpaatosLiitetiedot>

  @Output() startDone: EventEmitter<boolean> = new EventEmitter()

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  tilikausiAlkanut2024JalkeenObservable: Observable<boolean>

  latausVirhe: string

  readonly TilinpaatoksenTuloslaskelmanTyyppi = TilinpaatoksenTuloslaskelmanTyyppi

  readonly onkoMikro = signal(false)
  private readonly _injector = inject(Injector)
  private readonly _lukittu = signal(false)
  lomake = form(
    signal<AloitusLomake>({
      taseenLoppusummaYli: null,
      liikevaihtoYli: null,
      henkilostoYli: null,
      tilinpaatoksenTuloslaskelma: null,
      tilintarkastetaan: null,
      tilintarkastajat: [],
      allekirjoitetaanLemonaidissa: null,
    }),
    polku => {
      disabled(polku, () => this._lukittu())
      validoiReaktiivisesti(polku.tilinpaatoksenTuloslaskelma, Validators.required)
      validoiReaktiivisesti(polku.tilintarkastetaan, Validators.required)
      validoiReaktiivisesti(polku.allekirjoitetaanLemonaidissa, Validators.required)
    },
  )

  // Tilintarkastaja
  private _lataaTilintarkastajatSubject = new Subject<void>()
  tilintarkastajatObservable: Observable<ListausTilintarkastaja[]>
  readonly tilintarkastajatMap = signal(new Map<string, ListausTilintarkastaja>())
  suodatetutTilintarkastajatSubject: BehaviorSubject<ListausTilintarkastaja[]> = new BehaviorSubject<ListausTilintarkastaja[]>([])
  asiakasJaTilikausiStringObservable: Observable<string>
  tilintarkastajaKutsuOpen = false
  readonly TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_TAIKALINKKI = TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_TAIKALINKKI

  // Erillinen control autocomplete-haulle. Ei tallenneta liitetietoihin — käytetään vain chip-griin lisäämiseen.
  tilintarkastajaHaku = form(signal<string | ListausTilintarkastaja>(''), polku => {
    disabled(polku, () => this._lukittu())
  })
  @ViewChild('tilintarkastajaInput') tilintarkastajaInput: ElementRef<HTMLInputElement>

  // Debug things:
  liitetiedotUriObservable: Observable<string>
  liitetiedotEncodedUriObservable: Observable<string>
  kirjanpitajaOnDevaajaObservable: Observable<boolean> = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable

  lukittuObservable: Observable<boolean>

  private _cachedLiitetiedot: TilinpaatosLiitetiedot = this._tilinpaatosLiitetiedotJaettuService.annaTyhjaLiitetiedotObject(null, null)

  constructor(
    private _firebaseLemonator: FirebaseLemonator,
    private _asiakasService: AsiakasService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _tilinpaatosLiitetiedotJaettuService: TilinpaatosLiitetiedotJaettuService,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _copyService: KopioijaPalvelu,
    private _debugService: DebugService,
    private _timestampService: TimestampService,
    private _tilintarkastajaUriService: TilintarkastajaUriService,
    private _dateService: DateService,
    private _snackbar: MatSnackBar,
    private _ladataanService: LadataanService,
    private _dialog: MatDialog,
    private _stringService: StringService,
  ) {}

  ngOnInit() {
    this.tilikausiAlkanut2024JalkeenObservable = this.selectedTilikausiObservable.pipe(
      map(tilikausi => {
        return tilikausi?.alkaa?.year >= 2024
      }),
    )

    this.liitetiedotUriObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this.selectedTilikausiObservable]).pipe(
      map(([asiakas, tilikausi]) => {
        if (asiakas && tilikausi) {
          return this._kirjanpitoUriService.annaTilinpaatosLiitetiedotUri(asiakas.avain, tilikausi)
        }
        return ''
      }),
    )

    this.liitetiedotEncodedUriObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this.selectedTilikausiObservable]).pipe(
      map(([asiakas, tilikausi]) => {
        if (asiakas && tilikausi) {
          const uri = this._kirjanpitoUriService.annaTilinpaatosLiitetiedotUri(asiakas.avain, tilikausi)
          return this._debugService.createFirestoreLink(uri)
        }
        return ''
      }),
    )

    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    this.tilintarkastajatObservable = this._lataaTilintarkastajatSubject.pipe(
      startWith([]),
      switchMap(() => {
        this._ladataanService.aloitaLataaminen()
        return from(this._firebaseLemonator.functionsCall<TilintarkastajatAnnaListaRequest, TilintarkastajatAnnaListaResponse>('tilintarkastajatAnnaLista', {}))
      }),
      takeUntil(this._ngUnsubscribe),
      map(response => {
        if (response.e) {
          console.error('Tilintarkastajien haku epäonnistui:', response.e)
          return []
        }
        this._ladataanService.lopetaLataaminen()

        // Päivitetään map ja suodatetut
        this.tilintarkastajatMap.set(new Map(response.tilintarkastajat.map(t => [t.avain, t])))
        this.suodatetutTilintarkastajatSubject.next(response.tilintarkastajat)

        return response.tilintarkastajat
      }),
      lemonShare(),
    )

    this.asiakasJaTilikausiStringObservable = combineLatest([this._asiakasService.nykyinenAsiakasObservable, this.selectedTilikausiObservable]).pipe(
      takeUntil(this._ngUnsubscribe),
      map(([asiakas, tilikausi]) => {
        return `${asiakas.nimi}, tilikausi ${this._dateService.muotoilePaikallinenPaiva(tilikausi.alkaa, 'fi')} - ${this._dateService.muotoilePaikallinenPaiva(tilikausi.loppuu, 'fi')}`
      }),
    )

    muutokset(this.tilintarkastajaHaku)
      .pipe(debounceTime(50), takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this._paivitaTilintarkastajaListanSuodatus(value)
      })

    // Suodatuksesta poistetaan jo chippeihin valitut tilintarkastajat, joten kuunnellaan myös listan muutoksia.
    muutokset(this.lomake.tilintarkastajat)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => {
        this._paivitaTilintarkastajaListanSuodatus(this.tilintarkastajaHaku().value())
      })

    muutokset(this.lomake.taseenLoppusummaYli)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(val => (this._cachedLiitetiedot.valitut[TilinpaatosLiitetiedotKentat.ONKO_MIKROYRITYS_TASEEN_LOPPUSUMMA_YLI] = val ? true : null))
    muutokset(this.lomake.liikevaihtoYli)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(val => (this._cachedLiitetiedot.valitut[TilinpaatosLiitetiedotKentat.ONKO_MIKROYRITYS_LIIKEVAIHTO_YLI] = val ? true : null))
    muutokset(this.lomake.henkilostoYli)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(val => (this._cachedLiitetiedot.valitut[TilinpaatosLiitetiedotKentat.ONKO_MIKROYRITYS_HENKILOSTO_YLI] = val ? true : null))
    muutokset(this.lomake.tilintarkastetaan)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(val => this._tilintarkastetaanArvoMuuttui(val, this._cachedLiitetiedot))
    muutokset(this.lomake.allekirjoitetaanLemonaidissa)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(val => this._allekirjoitetaanLemonaidissaArvoMuuttui(val, this._cachedLiitetiedot))

    this.liitetiedotObservable
      .pipe(
        switchMap(liitetiedot => {
          if (!liitetiedot?.tilintarkastetaan) {
            return of({ liitetiedot, tilintarkastajat: [] as ListausTilintarkastaja[] })
          }
          return this.tilintarkastajatObservable.pipe(map(tilintarkastajat => ({ liitetiedot, tilintarkastajat })))
        }),
        take(1),
      )
      .subscribe(result => {
        if (!result.liitetiedot) {
          return
        }
        const { liitetiedot, tilintarkastajat } = result
        const avaimet = liitetiedot.tilintarkastajat ?? []
        const valitut: ListausTilintarkastaja[] = avaimet.map(avain => {
          const loytyy = tilintarkastajat.find(t => t.avain === avain)
          if (loytyy) {
            return loytyy
          }
          // Tilintarkastajaa ei löydy listalta (esim. poistettu) — säilytetään merkintä placeholderina, jotta avain ei katoa tallennuksessa. Chipissä näytetään tällöin vain avain.
          return { avain, etunimi: '', sukunimi: '', email: '', tila: null }
        })
        asetaArvo(this.lomake.tilintarkastajat, valitut)
      })

    combineLatest([this._asiakasService.nykyinenAsiakasObservable, this.selectedTilikausiObservable])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([asiakas, selected]) => {
        if (this.lomake.tilinpaatoksenTuloslaskelma().value()) {
          return
        }
        if (selected) {
          const tilikausiInFirestore = asiakas.tilikaudet.find(tk => tk.avain === selected.avain) // selectedTilikausiObservable is based on a manually updated Subject, i.e. does not contain up-to-date data so we're using asiakas.tilikaudet instead here
          if (tilikausiInFirestore.tilinpaatoksenTuloslaskelma) {
            /** Use saved value */
            asetaArvo(this.lomake.tilinpaatoksenTuloslaskelma, tilikausiInFirestore.tilinpaatoksenTuloslaskelma)
            return
          }
        }

        if (asiakas?.tilinpaatosRekisteroidaanBruttotuloslaskelmalla) {
          /** Use default settings, if available */
          asetaArvo(this.lomake.tilinpaatoksenTuloslaskelma, TilinpaatoksenTuloslaskelmanTyyppi.BRUTTO)
          return
        }
        /** Fall back to VIRALLINEN **/
        asetaArvo(this.lomake.tilinpaatoksenTuloslaskelma, TilinpaatoksenTuloslaskelmanTyyppi.VIRALLINEN)
      })

    combineLatest([muutokset(this.lomake.taseenLoppusummaYli), muutokset(this.lomake.liikevaihtoYli), muutokset(this.lomake.henkilostoYli)])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(vals => {
        this.onkoMikro.set(vals.filter(val => val)?.length < 2)
      })

    combineLatest([this.liitetiedotObservable, this.selectedTilikausiObservable])
      .pipe(take(1))
      .subscribe(([liitetiedot, tilikausi]) => {
        asetaArvo(this.lomake.taseenLoppusummaYli, !!liitetiedot?.valitut[TilinpaatosLiitetiedotKentat.ONKO_MIKROYRITYS_TASEEN_LOPPUSUMMA_YLI])
        asetaArvo(this.lomake.liikevaihtoYli, !!liitetiedot?.valitut[TilinpaatosLiitetiedotKentat.ONKO_MIKROYRITYS_LIIKEVAIHTO_YLI])
        asetaArvo(this.lomake.henkilostoYli, !!liitetiedot?.valitut[TilinpaatosLiitetiedotKentat.ONKO_MIKROYRITYS_HENKILOSTO_YLI])

        if (liitetiedot && liitetiedot.tilintarkastetaan !== undefined) {
          asetaArvo(this.lomake.tilintarkastetaan, !!liitetiedot.tilintarkastetaan)
        } else {
          asetaArvo(this.lomake.tilintarkastetaan, null)
        }
        if (liitetiedot && liitetiedot.allekirjoitetaanLemonaidissa !== undefined) {
          asetaArvo(this.lomake.allekirjoitetaanLemonaidissa, !!liitetiedot.allekirjoitetaanLemonaidissa)
        } else {
          asetaArvo(this.lomake.allekirjoitetaanLemonaidissa, null)
        }

        this._cachedLiitetiedot = liitetiedot ? this._copyService.cloneObjectDeep(liitetiedot) : this._tilinpaatosLiitetiedotJaettuService.annaTyhjaLiitetiedotObject(tilikausi, null)
      })

    this.lukittuObservable = this.tilinpaatosStatusObservable.pipe(map(status => !!status?.rekisteroity))

    this.lukittuObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(lukittu => {
      this._lukittu.set(lukittu)
      // Reaktiivisen lomakkeen disable() ja enable() ilmoittivat kenttien nykyiset arvot tilaajille kenttä kerrallaan
      this._ilmoitaNykyinenArvo(this.lomake.taseenLoppusummaYli)
      this._ilmoitaNykyinenArvo(this.lomake.liikevaihtoYli)
      this._ilmoitaNykyinenArvo(this.lomake.henkilostoYli)
      this._ilmoitaNykyinenArvo(this.lomake.tilinpaatoksenTuloslaskelma)
      this._ilmoitaNykyinenArvo(this.lomake.tilintarkastetaan)
      this._ilmoitaNykyinenArvo(this.lomake.tilintarkastajat)
      this._ilmoitaNykyinenArvo(this.lomake.allekirjoitetaanLemonaidissa)
      this._ilmoitaNykyinenArvo(this.tilintarkastajaHaku)
    })
  }

  async _tilintarkastetaanArvoMuuttui(tarkastetaan: boolean, liitetiedot: TilinpaatosLiitetiedot) {
    liitetiedot.tilintarkastetaan = !!tarkastetaan
    liitetiedot.valitut[TilinpaatosLiitetiedotKentat.TTL3_4] = !!tarkastetaan

    if (tarkastetaan) {
      asetaArvo(this.lomake.allekirjoitetaanLemonaidissa, false)
    } else {
      asetaArvo(this.lomake.tilintarkastajat, [])
    }
  }

  async _allekirjoitetaanLemonaidissaArvoMuuttui(allekirjoitetaan: boolean, liitetiedot: TilinpaatosLiitetiedot) {
    liitetiedot.allekirjoitetaanLemonaidissa = !!allekirjoitetaan
    liitetiedot.valitut[TilinpaatosLiitetiedotKentat.ALLEKIRJOITUSTEN_TEKSTI] = !allekirjoitetaan

    if (allekirjoitetaan) {
      asetaArvo(this.lomake.tilintarkastetaan, false)
    }
  }

  // Valitaan hakukentästä uusi tilintarkastja, joka lisätään chippeihin
  lisaaTilintarkastaja(tilintarkastaja: ListausTilintarkastaja) {
    if (!tilintarkastaja || !tilintarkastaja.avain) {
      return
    }
    const valitut = this.lomake.tilintarkastajat().value() ?? []
    if (valitut.some(t => t.avain === tilintarkastaja.avain)) {
      return
    }
    asetaArvo(this.lomake.tilintarkastajat, [...valitut, tilintarkastaja])
    asetaArvo(this.tilintarkastajaHaku, '')
  }

  // Poistetaan chipeistä tilintarkastaja
  poistaTilintarkastaja(tilintarkastaja: ListausTilintarkastaja) {
    if (!tilintarkastaja) {
      return
    }
    const valitut = this.lomake.tilintarkastajat().value() ?? []
    asetaArvo(
      this.lomake.tilintarkastajat,
      valitut.filter(t => t.avain !== tilintarkastaja.avain),
    )
  }

  hylatytValitut(): ListausTilintarkastaja[] {
    const valitut = this.lomake.tilintarkastajat().value() ?? []
    return valitut.filter(t => this.tilintarkastajatMap().get(t.avain)?.tila === 'hylatty')
  }

  private _paivitaTilintarkastajaListanSuodatus(haku: string | ListausTilintarkastaja) {
    const kaikki = Array.from(this.tilintarkastajatMap().values())
    const valitutAvaimet = new Set((this.lomake.tilintarkastajat().value() ?? []).map(t => t.avain))
    const ilmanValittuja = kaikki.filter(t => !valitutAvaimet.has(t.avain))
    if (this._stringService.isString(haku) && haku) {
      const hakuLowerCase = haku.toLowerCase()
      const suodatetut = ilmanValittuja.filter(t => `${t.etunimi} ${t.sukunimi}`.toLowerCase().includes(hakuLowerCase))
      this.suodatetutTilintarkastajatSubject.next(suodatetut)
    } else {
      this.suodatetutTilintarkastajatSubject.next(ilmanValittuja)
    }
  }

  // Pidetään autocomplete-input tyhjänä — valinnat menevät chippeihin, eivät input-kenttään.
  displayTyhja(): string {
    return ''
  }

  async avaaTilintarkastajaKutsuDialogi() {
    try {
      // Hae tarvittavat tiedot
      const asiakasPromise = firstValueFrom(this._asiakasService.nykyinenAsiakasObservable)
      const tilikausiPromise = firstValueFrom(this.selectedTilikausiObservable)
      const [asiakas, tilikausi] = await Promise.all([asiakasPromise, tilikausiPromise])

      // Avaa dialogi
      const dialogRef = this._dialog.open(TilintarkastajaKutsuDialogComponent, {
        width: '800px',
        data: {
          asiakasAvain: asiakas.avain,
          tilikausiAvain: tilikausi.avain,
        },
      })

      // Kun dialogi sulkeutuu, päivitä tilintarkastajat jos kutsu onnistui
      dialogRef.afterClosed().subscribe(onnistui => {
        if (onnistui) {
          this._lataaTilintarkastajatSubject.next()
        }
      })
    } catch (err) {
      console.error('Dialogin avaaminen epäonnistui', err)
      this._snackbar.open('Dialogin avaaminen epäonnistui', null, { duration: 5000 })
    }
  }

  private _ilmoitaNykyinenArvo<T>(kentta: FieldTree<T>) {
    asetaArvo(kentta, kentta().value())
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  onkoVirheita<T>(kentta: FieldTree<T>): boolean {
    return kentta().errors().length > 0
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  async aloitaTilinpaatos() {
    this._ladataanService.aloitaLataaminen()

    const asiakasPromise = firstValueFrom(this._asiakasService.nykyinenAsiakasObservable)
    const tilikausiPromise = firstValueFrom(this.selectedTilikausiObservable)
    const kirjanpitajaPromise = this._kirjautunutKayttajaService.getKirjanpitajanTiedot()

    const [asiakas, tilikausi, kirjanpitaja] = await Promise.all([asiakasPromise, tilikausiPromise, kirjanpitajaPromise])

    for (const tk of asiakas.tilikaudet) {
      if (tk.avain === tilikausi?.avain) {
        tk.tilinpaatosStarted = 1
        tk.tilinpaatoksenTuloslaskelma = this.lomake.tilinpaatoksenTuloslaskelma().value()
        break
      }
    }

    const tallennettava = this._copyService.cloneObjectDeep(this._cachedLiitetiedot)
    this._tilinpaatosLiitetiedotJaettuService.valmisteleTallennettava(tallennettava, kirjanpitaja.uid, tilikausi)

    /** Delete fields entirely if necessary to enable saving the liitetiedot object */
    if (tallennettava.osingotNostettavissa === undefined) {
      delete tallennettava.osingotNostettavissa
    }
    if (tallennettava.osingotNostettavissaPvm === undefined) {
      delete tallennettava.osingotNostettavissaPvm
    }

    tallennettava.tilintarkastetaan = !!tallennettava.tilintarkastetaan
    const valitutTilintarkastajat = this.lomake.tilintarkastajat().value() ?? []
    tallennettava.tilintarkastajat = tallennettava.tilintarkastetaan ? valitutTilintarkastajat.map(t => t.avain) : []

    tallennettava.valitut[TilinpaatosLiitetiedotKentat.TTL3_4] = tallennettava.tilintarkastetaan ? true : this._firebaseLemonator.firestoreDeleteMarker()

    tallennettava.allekirjoitetaanLemonaidissa = !!tallennettava.allekirjoitetaanLemonaidissa
    tallennettava.valitut[TilinpaatosLiitetiedotKentat.ALLEKIRJOITUSTEN_TEKSTI] = !tallennettava.allekirjoitetaanLemonaidissa ? true : this._firebaseLemonator.firestoreDeleteMarker()

    tallennettava.valitut[TilinpaatosLiitetiedotKentat.ONKO_MIKROYRITYS_TASEEN_LOPPUSUMMA_YLI] = tallennettava.valitut[TilinpaatosLiitetiedotKentat.ONKO_MIKROYRITYS_TASEEN_LOPPUSUMMA_YLI]
      ? true
      : this._firebaseLemonator.firestoreDeleteMarker()
    tallennettava.valitut[TilinpaatosLiitetiedotKentat.ONKO_MIKROYRITYS_LIIKEVAIHTO_YLI] = tallennettava.valitut[TilinpaatosLiitetiedotKentat.ONKO_MIKROYRITYS_LIIKEVAIHTO_YLI]
      ? true
      : this._firebaseLemonator.firestoreDeleteMarker()
    tallennettava.valitut[TilinpaatosLiitetiedotKentat.ONKO_MIKROYRITYS_HENKILOSTO_YLI] = tallennettava.valitut[TilinpaatosLiitetiedotKentat.ONKO_MIKROYRITYS_HENKILOSTO_YLI]
      ? true
      : this._firebaseLemonator.firestoreDeleteMarker()

    const batch = this._firebaseLemonator.firestoreBatch()
    const uri = this._kirjanpitoUriService.annaTilinpaatosLiitetiedotUri(asiakas.avain, tilikausi)
    batch.set(uri, tallennettava, { merge: true })

    const historiakopio: TilinpaatosLiitetiedot = this._copyService.cloneObjectDeep(tallennettava)
    historiakopio.avain = this._firebaseLemonator.firestoreCreateId()
    const historiaUri = this._kirjanpitoUriService.annaTilinpaatosLiitetiedotHistoriaUri(asiakas.avain, tilikausi, historiakopio.avain)
    batch.set(historiaUri, historiakopio)

    await batch.commit()
    await this._asiakasService.paivitaAsiakas(asiakas, 'tilikaudet-eivat-voineet-muuttua', false, false)

    try {
      const tilintarkastajaOikeusResult = await this._firebaseLemonator.functionsCall<TilintarkastajatAsetaOikeusRequest, TilintarkastajatAsetaOikeusResponse>('tilintarkastajatAsetaOikeus', {
        tilintarkastajat: tallennettava.tilintarkastajat,
        asiakasAvain: asiakas.avain,
        tilikausiAvain: tilikausi.avain,
      })
      if (tilintarkastajaOikeusResult.e) {
        throw new Error(tilintarkastajaOikeusResult.e)
      }
    } catch (error) {
      console.error('Tilintarkastajan oikeuden asettaminen epäonnistui:', error)
      this._ladataanService.lopetaLataaminen()
      this._snackbar.open('Liitetiedot tallennettu, mutta tilintarkastajan oikeuden asettaminen epäonnistui. Tallenna liitetiedot uudelleen.', 'OK')
      return
    }

    this._ladataanService.lopetaLataaminen()
    return this.startDone.emit(true)
  }
}
